import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowDown, ArrowUp, PencilSimple, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { adjustStock, pickStock, receiveStock, isEagleInventory } from "@/lib/inventoryApi";
import { formatApiErrorDetail } from "@/lib/apiClient";

/**
 * Receive / Pick / Adjust stock from the SKU-lookup result.
 *
 * Eagle SKUs (inventory_type === "components") REQUIRE a component selector —
 * the Submit button stays disabled until Frames / Doors / Both is picked.
 * Regular SKUs don't show the selector at all.
 *
 * On success the parent is notified via onDone() so it can re-run
 * `productLookup` and refresh the counts from source of truth (there is no
 * `new_stock` field on Eagle responses). On a 409 "insufficient stock" from
 * Base44 we also trigger onDone() so the user sees the current counts
 * before deciding whether to retry.
 */
const ACTIONS = [
  { key: "receive", label: "Receive",  icon: ArrowDown,     tone: "emerald", desc: "Add stock from an incoming shipment" },
  { key: "pick",    label: "Pick",     icon: ArrowUp,       tone: "amber",   desc: "Pull stock for an order or build" },
  { key: "adjust",  label: "Adjust",   icon: PencilSimple,  tone: "zinc",    desc: "Set the on-hand count to a new value" },
];

const COMPONENTS = [
  { key: "frame", label: "Frames",    hint: "Face-frame fronts" },
  { key: "door",  label: "Door Sets", hint: "Complete door front sets" },
  { key: "both",  label: "Both",      hint: "Same qty applied to each" },
];

export default function StockMutateForm({ product, onDone }) {
  const eagle = isEagleInventory(product);
  const [action, setAction] = useState("receive");
  const [component, setComponent] = useState(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset selection whenever the SKU changes.
  useEffect(() => {
    setAction("receive");
    setComponent(null);
    setQty("");
    setError(null);
    setSuccess(null);
  }, [product?.sku]);

  // "Both" helper text — differs between adjust and receive/pick per spec.
  const bothHelper = action === "adjust"
    ? "Sets BOTH frames and door sets to the same number. For different totals, adjust each one separately."
    : "Applies the same quantity to frames AND door sets.";

  const qtyNum = qty === "" ? null : Number(qty);
  const qtyValid =
    qtyNum != null &&
    Number.isFinite(qtyNum) &&
    Number.isInteger(qtyNum) &&
    qtyNum >= 0 &&
    (action === "adjust" || qtyNum >= 1);

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!qtyValid) return false;
    if (eagle && !component) return false;
    return true;
  }, [busy, qtyValid, eagle, component]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const args = { sku: product.sku };
      if (eagle) args.component = component;
      if (action === "adjust") args.new_quantity = qtyNum;
      else args.quantity = qtyNum;
      const fn = action === "receive" ? receiveStock : action === "pick" ? pickStock : adjustStock;
      const result = await fn(args);
      setSuccess(result);
      setQty("");
      setComponent(null);
      onDone?.({ result, mustRefresh: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError({ status, detail });
      // On 409 (insufficient stock / concurrent change) re-run lookup so the
      // user sees current counts before deciding whether to retry.
      if (status === 409) onDone?.({ mustRefresh: true });
    } finally {
      setBusy(false);
    }
  }

  const activeAction = ACTIONS.find((a) => a.key === action);

  return (
    <Card className="p-5" data-testid="stock-mutate-form">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="font-heading font-black text-lg">Update stock</h3>
        {eagle && (
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-indigo-700 dark:text-indigo-300">
            Component required
          </span>
        )}
      </div>

      {/* Action tabs */}
      <div className="grid grid-cols-3 gap-2 mb-4" role="tablist" aria-label="Stock action">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const active = a.key === action;
          return (
            <button
              key={a.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => { setAction(a.key); setError(null); setSuccess(null); }}
              className={[
                "flex flex-col items-center gap-1 py-3 px-2 rounded-md border transition",
                active
                  ? "border-[#C8102E] bg-[#C8102E]/5 shadow-sm"
                  : "border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/40",
              ].join(" ")}
              data-testid={`stock-action-${a.key}`}
            >
              <Icon size={18} weight="bold" className={active ? "text-[#C8102E]" : "text-zinc-500"} />
              <span className={`text-sm font-bold ${active ? "text-[#C8102E]" : ""}`}>{a.label}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-zinc-500 mb-4">{activeAction.desc}</p>

      <form onSubmit={submit} className="space-y-4">
        {/* Component selector — Eagle only, required, no default. */}
        {eagle && (
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">
              Component <span className="text-[#C8102E]">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {COMPONENTS.map((c) => {
                const active = c.key === component;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => { setComponent(c.key); setError(null); }}
                    className={[
                      "flex flex-col items-start text-left gap-0.5 py-2.5 px-3 rounded-md border transition",
                      active
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40"
                        : "border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/40",
                    ].join(" ")}
                    data-testid={`stock-component-${c.key}`}
                  >
                    <span className={`text-sm font-bold ${active ? "text-indigo-700 dark:text-indigo-300" : ""}`}>
                      {c.label}
                    </span>
                    <span className="text-[11px] text-zinc-500">{c.hint}</span>
                  </button>
                );
              })}
            </div>
            {component === "both" && (
              <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-2" data-testid="stock-component-both-helper">
                {bothHelper}
              </p>
            )}
          </div>
        )}

        {/* Current counts chip — shown on Eagle so the user has context. */}
        {eagle && (
          <div className="flex flex-wrap gap-2 text-[11px]" data-testid="stock-current-counts">
            <CountChip label="F" value={product.eagle_frame_stock_quantity ?? 0} />
            <CountChip label="D" value={product.eagle_door_stock_quantity ?? 0} />
            <CountChip label="Sets" value={product.available_complete_sets ?? 0} highlight />
          </div>
        )}

        {/* Quantity */}
        <div>
          <Label htmlFor="stock-qty" className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">
            {action === "adjust" ? "New quantity" : "Quantity"}
          </Label>
          <Input
            id="stock-qty"
            type="number"
            inputMode="numeric"
            min={action === "adjust" ? 0 : 1}
            step={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder={action === "adjust" ? "e.g. 20" : "e.g. 5"}
            className="h-10 font-mono text-base"
            data-testid="stock-qty-input"
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            Whole numbers only. {action === "adjust" ? "0 is allowed." : "Must be at least 1."}
          </p>
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-[#C8102E] hover:bg-[#A00D25] h-11 w-full disabled:opacity-50"
          data-testid="stock-submit"
        >
          {busy ? "Submitting…" : `${activeAction.label} ${qtyNum || ""}${eagle && component ? ` · ${component.toUpperCase()}` : ""}`.trim()}
        </Button>
      </form>

      {error && (
        <div
          className="mt-4 p-3 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 text-sm text-red-800 dark:text-red-200 flex items-start gap-2"
          data-testid="stock-error"
        >
          <WarningCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-bold">
              {error.status === 409 ? "Insufficient stock" :
               error.status === 400 ? "Request rejected" :
               error.status ? `Error ${error.status}` : "Error"}
            </div>
            <div className="opacity-90 break-words">{error.detail}</div>
            {error.status === 409 && (
              <div className="text-xs opacity-75 mt-1">Counts have been refreshed above. Review them before retrying.</div>
            )}
          </div>
        </div>
      )}

      {success && !error && (
        <div
          className="mt-4 p-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 text-sm text-emerald-800 dark:text-emerald-200 flex items-start gap-2"
          data-testid="stock-success"
        >
          <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div>
            {success.action.toUpperCase()} recorded
            {success.component ? ` · ${String(success.component).toUpperCase()}` : ""}
            {success.warning ? ` — ${success.warning}` : ""}
          </div>
        </div>
      )}
    </Card>
  );
}

function CountChip({ label, value, highlight }) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 px-2 py-1 rounded font-mono",
        highlight
          ? "bg-[#C8102E]/10 text-[#C8102E] font-bold"
          : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300",
      ].join(" ")}
    >
      <span className="text-[9px] uppercase tracking-[0.15em] font-bold opacity-60">{label}</span>
      <span className="text-sm font-black">{value}</span>
    </span>
  );
}
